import React, {useEffect} from "react";
import Head from 'next/head'
import {GoogleTagManager} from "@App/components/common/GoogleTagManager";
import {apolloClient} from "@App/graphql/client";
import {GET_GARAGE_LIST, GET_GARAGE_DETAILS, GET_GARAGES, GET_BRAND_LIST} from "@App/graphql/queries";
import {Brand, Garage, GarageSiteMapItem, PartnerConfig, Slot} from "@App/types";
import useStore from "@App/store";
import {GarageProfileFooter} from "@App/components/GarageProfile/GarageProfileFooter";
import {SchemaMarkup} from "@App/components/common/SchemaMarkup";
import {GarageProfileCalendarProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileMobileBooking";
import dynamic from "next/dynamic";
import {GarageProfileHeaderProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileHeader";
import {GarageProfileBacklinkProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileBacklink";
import {GarageProfileHeadlineProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileHeadline";
import {GarageProfileHeroProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileHero";
import {GarageProfileBookingProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileBooking";
import {GarageProfileOfferingsProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileOfferings";
import {GarageProfileMOTProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileMOT";
import {GarageProfileMapLocationProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileMapLocation";
import {
    GarageProfileOpeninghoursProps
} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileOpeninghours";
import {
    GarageProfilePickupServiceProps
} from "@App/components/GarageProfile/GaragProfileParts/GarageProfilePickupService";
import {GarageProfileAboutProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileAbout";
import {GarageProfileRatingsProps} from "@App/components/GarageProfile/GaragProfileParts/GarageProfileRatings";
import {Text} from "@eo-locale/react";
import {partnerConfigs} from "@App/hooks/config/partners";

const GarageProfileHeader = dynamic<GarageProfileHeaderProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileHeader").then(mod => mod.GarageProfileHeader))
const GarageProfileHeadline = dynamic<GarageProfileHeadlineProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileHeadline").then(mod => mod.GarageProfileHeadline))
const GarageProfileHero = dynamic<GarageProfileHeroProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileHero").then(mod => mod.GarageProfileHero))
const GarageProfileBooking = dynamic<GarageProfileBookingProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileBooking").then(mod => mod.GarageProfileBooking))
const GarageProfileOfferings = dynamic<GarageProfileOfferingsProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileOfferings").then(mod => mod.GarageProfileOfferings))
const GarageProfileMOT = dynamic<GarageProfileMOTProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileMOT").then(mod => mod.GarageProfileMOT))
const GarageProfileMapLocation = dynamic<GarageProfileMapLocationProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileMapLocation").then(mod => mod.GarageProfileMapLocation))
const GarageProfileOpeninghours = dynamic<GarageProfileOpeninghoursProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileOpeninghours").then(mod => mod.GarageProfileOpeninghours))
const GarageProfilePickupService = dynamic<GarageProfilePickupServiceProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfilePickupService").then(mod => mod.GarageProfilePickupService))
const GarageProfileAbout = dynamic<GarageProfileAboutProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileAbout").then(mod => mod.GarageProfileAbout))
const GarageProfileMobileBooking = dynamic<GarageProfileCalendarProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileMobileBooking").then(mod => mod.GarageProfileMobileBooking))
const GarageProfileRatings = dynamic<GarageProfileRatingsProps>(import("@App/components/GarageProfile/GaragProfileParts/GarageProfileRatings").then(mod => mod.GarageProfileRatings))

export const getStaticPaths = async () => {
    const {data} = await apolloClient.query({
        query: GET_GARAGE_LIST
    })

    const partners = partnerConfigs;

    function mapPaths(partners: PartnerConfig[], garage: GarageSiteMapItem[]) {
        var mappedPaths: any = [];
        partners.forEach((partner: PartnerConfig) => {
            garage.forEach((garage:GarageSiteMapItem) => {
                mappedPaths.push(
                    {params: {
                            partner: partner.slug,
                            city:  garage.profileSlug.split('/')[0],
                            slug: garage.profileSlug.split('/')[1]
                        }}
                )
            })
        })
        return mappedPaths;
    }

    return {
        paths: mapPaths(partners, data.organization.garageSiteMap
            .filter((garage:GarageSiteMapItem) => (garage.profileSlug.split('/').length > 1))),
        fallback: 'blocking'
    }
}

export const getStaticProps = async (context: any) => {
    const city = context.params.city
    const slug = context.params.slug
    const id = slug.split('-')[0]

    if (Number.isNaN(Number(id))) {
        return {
            notFound: true,
        }
    }

    let nearbyGarages

    const config = partnerConfigs.filter((config: PartnerConfig) => config.slug === context.params.partner)

    const garage = await apolloClient.query({
        query: GET_GARAGE_DETAILS,
        variables: {
            organization: config[0].id,
            id
        },
        fetchPolicy: 'no-cache'
    }).then(result => result.data.organization.garages.data.length > 0 ? result.data.organization.garages.data[0] : null)


    if (!garage) {
        return {
            notFound: true,
        }
    }

    if (slug !== id + '-' + garage.slug || city !== garage.profileSlug?.split('/')[0]) {
        return {
            redirect: {
                permanent: true,
                destination: `/werkstatt/${garage.profileSlug?.split('/')[0]}/${garage.id}-${garage.profileSlug?.split('/')[1]}/`
            },
        };
    }

    nearbyGarages = await apolloClient.query({
        query: GET_GARAGES,
        variables: {
            organization: config[0].id,
            lat: garage.lat,
            lon: garage.lon,
            distance: 50000,
            sortBy : "smallest_distance",
            limit : 10,
            withCalculation: false,
            withTravelTime: false
        },
    })

    const {data: brandsData} = await apolloClient
        .query({
            query: GET_BRAND_LIST
        })



    return {
        props: {
            garage : garage,
            nearbyGarages: nearbyGarages?.data.organization.garages.data.filter((g: Garage) => g.id !== garage.id),
            partnerConfig: config[0],
            brands: brandsData.brands
        },
        revalidate: 3600, // In seconds
    }
}

type GarageProps = {
    garage: Garage
    nearbyGarages: Garage[]
    partnerConfig: PartnerConfig
    brands: Brand[]
}

const Profile = React.memo(({garage, nearbyGarages, partnerConfig, brands}: GarageProps) => {

    const state = useStore()

    useEffect(() => {
        state.setGarage(garage)
    }, [])

    useEffect(() => {
        const id = crypto.randomUUID();
        {garage?.id && (
            window.sessionStorage.setItem(`correlationID${garage?.id}`, `${id}`)
        )}
    }, [garage])

    const onSubmit = (slot: Slot) => {

    }

    if (!garage) {
        return <></>
    }

    return (
        <div data-theme={partnerConfig.name}>
            <Head>
                <title>{`${garage.name} in ${garage.city}`}</title>
                <meta name="description"
                      content={`${garage.name} direkt online buchen - Services der Autowerkstatt ${garage.name} in ${garage.city}, Bewertungen und weitere KFZ Werkstätten in ${garage.city} finden`}/>
                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png"/>
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png"/>
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png"/>
                <link rel="manifest" href="/site.webmanifest"/>
                <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5"/>
                <meta name="msapplication-TileColor" content="#da532c"/>
                <meta name="theme-color" content="#ffffff"/>
                <link rel="preconnect" href="https://maps.gstatic.com"/>
                <link rel="preconnect" href="https://maps.googleapis.com"/>
                <link rel="preconnect" href="https://fonts.googleapis.com"/>
                <meta name="viewport" content="width=device-width, initial-scale=1"/>
                <meta name="canonical" content={`${process.env.NEXT_PUBLIC_BASE_URL}${partnerConfig.slug}/${garage.profileSlug?.split('/')[0]}/${garage.id}-${garage.profileSlug?.split('/')[1]}/`}/>
                {!partnerConfig.profilePagesIndexable && (
                    <meta name="robots" content="noindex, follow"/>
                )}
            </Head>
            <GoogleTagManager/>
                <GarageProfileHeader partner={partnerConfig}/>
            {garage && (
                    <main>
                        <div className={'garageProfile grid grid-cols-12 lg:max-w-[928px] lx:max-w-screen-lgplus my-0 mx-6 md:mx-12 lg:mx-auto'}>
                            {/*<GarageProfileBacklink city={garage.baseCity} garagecount={state.garagecount}/>*/}
                            <GarageProfileHeadline garage={garage}/>
                            <GarageProfileHero garage={garage} partner={partnerConfig}/>

                            <div className={'garageProfile__content col-span-full grid grid-cols-12 lg:gap-x-16 order-1'}>
                                <div className={'col-span-12 lg:col-span-7 grid grid-cols-12 order-2 lg:order-1'}>
                                    {/*<GarageProfileTypeInformation/>*/}
                                    <GarageProfileOfferings garage={garage}/>
                                    <GarageProfileMOT garage={garage}/>
                                    <GarageProfileMapLocation garage={garage}/>
                                    <GarageProfileOpeninghours garage={garage}/>
                                    {garage.premium_services && garage.premium_services.some(premiumService => (premiumService.type === 'CollectAndReturn')) && (
                                        <GarageProfilePickupService/>
                                    )}
                                    <GarageProfileAbout garage={garage}/>
                                    {garage.reviews?.distribution &&
                                        (<GarageProfileRatings averageRating={garage.rating} ratingCount={garage.review_count} garageName={garage.name} ratings={garage.reviews}/>)
                                    }
                                    {partnerConfig.shouldDisplayQuotation && (
                                        <div className={'col-span-full mb-6'}>
                                            <h2 className={'text-3xl font-serif text-gray-900 font-semibold'}><Text id={'costevaluation_garage'} name={garage.name}/></h2>
                                            <a href="#booking" className={'relative text-white text-base leading-6 font-medium no-underline block w-full bg-primary-500 px-12 py-2 mt-5 rounded-lg text-center'}><Text id={'costevaluation_now'}/></a>
                                        </div>
                                    )}
                                </div>

                                <div className={'col-span-12 grid lg:col-span-5 grid-cols-12 order-1 lg:order-2'}>
                                    <GarageProfileBooking garage={garage} id={'booking'} onSubmit={onSubmit} brands={brands} partner={partnerConfig}/>
                                </div>

                                <div className={'bg-white w-full fixed left-0 bottom-0 md:invisible grid col-span-12 grid-cols-12 z-10'}>
                                    <GarageProfileMobileBooking garage={garage}/>
                                </div>
                            </div>
                        </div>
                    </main>
                )}

            {partnerConfig.shouldDisplayFooter && (
                 <GarageProfileFooter garages={nearbyGarages} current={garage} partner={partnerConfig}/>
            )}
            <SchemaMarkup garage={garage}/>
        </div>
    )
})
Profile.displayName = 'Garage'
export default Profile
